import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { fetchBalances, fetchNextNonce, fetchTransactions, fetchTx, fetchChainTip } from "./api";
import {
  buildAccount,
  buildNeighbors,
  buildTxDetail,
  mapTxRows,
  type NodeData,
  type TxDetail,
} from "./model";

export const EXAMPLE_ADDRESS = "SM1Z6BP8PDKYKXTZXXSKXFEY6NQ7RAM7DAEAYR045";

const nodeCache = new Map<string, Promise<NodeData>>();
const txCache = new Map<string, Promise<TxDetail>>();

const TTL_NODE = 5 * 60 * 1000;
const TTL_TX = 30 * 60 * 1000;

function cacheGet<T>(key: string, ttl: number): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const { t, v } = JSON.parse(raw) as { t: number; v: T };
    if (Date.now() - t > ttl) {
      sessionStorage.removeItem(key);
      return null;
    }
    return v;
  } catch {
    return null;
  }
}

function cacheSet(key: string, v: unknown): void {
  const payload = JSON.stringify({ t: Date.now(), v });
  try {
    sessionStorage.setItem(key, payload);
  } catch {
    // Quota hit: drop our own cached entries and try once more.
    try {
      for (const k of Object.keys(sessionStorage)) {
        if (k.startsWith("st:")) sessionStorage.removeItem(k);
      }
      sessionStorage.setItem(key, payload);
    } catch {
      /* storage unavailable - fall back to in-memory only */
    }
  }
}

async function loadNodeUncached(principal: string): Promise<NodeData> {
  const [balances, nonce, page] = await Promise.all([
    fetchBalances(principal),
    fetchNextNonce(principal),
    fetchTransactions(principal, 50),
  ]);
  const account = buildAccount(principal, balances.stx?.balance, balances.stx?.total_fees_sent, page.total, nonce, page.txs);
  return {
    account,
    neighbors: buildNeighbors(page.txs, principal),
    txs: mapTxRows(page.txs, principal),
  };
}

function loadNode(principal: string): Promise<NodeData> {
  const hit = nodeCache.get(principal);
  if (hit) return hit;
  const stored = cacheGet<NodeData>("st:node:" + principal, TTL_NODE);
  if (stored) {
    const p = Promise.resolve(stored);
    nodeCache.set(principal, p);
    return p;
  }
  const p = loadNodeUncached(principal)
    .then((d) => {
      cacheSet("st:node:" + principal, d);
      return d;
    })
    .catch((err) => {
      nodeCache.delete(principal);
      throw err;
    });
  nodeCache.set(principal, p);
  return p;
}

function txDetail(txid: string): Promise<TxDetail> {
  const hit = txCache.get(txid);
  if (hit) return hit;
  const stored = cacheGet<TxDetail>("st:tx:" + txid, TTL_TX);
  if (stored) {
    const p = Promise.resolve(stored);
    txCache.set(txid, p);
    return p;
  }
  const p = Promise.all([fetchTx(txid), fetchChainTip()])
    .then(([tx, tip]) => {
      const det = buildTxDetail(tx, tip);
      cacheSet("st:tx:" + txid, det);
      return det;
    })
    .catch((err) => {
      txCache.delete(txid);
      throw err;
    });
  txCache.set(txid, p);
  return p;
}

function escHtml(s: string): string {
  return s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" })[c] ?? c);
}

function toast(message: string, accent = "#f5741e"): void {
  Toastify({
    text:
      '<span style="display:inline-flex;align-items:center;gap:9px">' +
      '<span style="width:7px;height:7px;border-radius:50%;flex:none;background:' +
      accent +
      '"></span><span>' +
      escHtml(message) +
      "</span></span>",
    escapeMarkup: false,
    duration: 3200,
    gravity: "bottom",
    position: "right",
    close: false,
    style: {
      background: "#ffffff",
      border: "1px solid rgba(0,0,0,.08)",
      borderRadius: "12px",
      color: "rgba(23,19,16,.82)",
      boxShadow: "0 12px 34px rgba(0,0,0,.12)",
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
      fontSize: "12px",
      fontWeight: "500",
      letterSpacing: "0",
      padding: "12px 16px",
    },
  }).showToast();
}

export interface StacksTreeApi {
  EXAMPLE_ADDRESS: string;
  loadNode: (principal: string) => Promise<NodeData>;
  txDetail: (txid: string) => Promise<TxDetail>;
  chainTip: () => Promise<number>;
  toast: (message: string, accent?: string) => void;
}

const api: StacksTreeApi = { EXAMPLE_ADDRESS, loadNode, txDetail, chainTip: fetchChainTip, toast };

declare global {
  interface Window {
    StacksTree: StacksTreeApi;
  }
}

window.StacksTree = api;
